"use client"

import { withVaccineFilter } from "@/consts/query-params"
import { trackEvent } from "@/lib/amplitude/amplitude"
import { urlParser } from "@/lib/urlParser"
import { useStoryStore } from "@/store/story-store"
import { cn } from "@/utils/utils"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useEffect, useRef, useState } from "react"
import { IframeModal } from "../iframe/IframeModal"

type FloatingCtaContent = {
  disable_floating_cta?: boolean
  custom_floating_cta?: boolean
  floating_cta_label?: string
  floating_cta_link?: { cached_url?: string }
}

export const VaccinationFloatingCta = ({
  className,
  linkClassName,
  href,
  vaccineSlug,
  openInIframe = false,
  trackingEvent
}: {
  /** Styles the fixed/sticky wrapper — use for visibility, e.g. `lg:hidden`. */
  className?: string
  /** Styles the pill itself — use for sizing, e.g. `min-w-48`. */
  linkClassName?: string
  href?: string
  /**
   * The vaccine this page is about, e.g. "tbe". Appended as `?vaccin=` so the clinics overview
   * opens pre-filtered to it — the same thing the "Boka tid" button on a price card does.
   *
   * Only passed from a vaccine's own page. A listing has no single vaccine, and a clinic page's
   * link goes to MittVaccin, which knows nothing about this param.
   */
  vaccineSlug?: string
  /**
   * Open the destination in a modal iframe instead of navigating. Used on clinic pages, where the
   * target is the clinic's MittVaccin booking flow and the reader should keep their place on the page.
   */
  openInIframe?: boolean
  trackingEvent: string
}) => {
  const story = useStoryStore(state => state.story)
  const content = story?.content as FloatingCtaContent | undefined
  const pathname = usePathname()

  // Fade out while actively scrolling; fade back in shortly after scrolling stops.
  const [isScrolling, setIsScrolling] = useState(false)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [open, setOpen] = useState(false)

  const handleOnIframeButtonClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (trackingEvent) trackEvent(trackingEvent, { page_path: pathname })
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
    e.preventDefault()
    e.stopPropagation()
    setOpen(true)
  }

  useEffect(() => {
    const onScroll = () => {
      setIsScrolling(true)
      if (idleTimer.current) clearTimeout(idleTimer.current)
      idleTimer.current = setTimeout(() => setIsScrolling(false), 150)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (idleTimer.current) clearTimeout(idleTimer.current)
    }
  }, [])

  // A derived `href` (a clinic's booking URL, built from its `mittvaccin_clinic_id`) is the default.
  // The CMS link is an explicit per-story override, opted into via `custom_floating_cta` the same way
  // CTAProvider treats that flag — so editing it in Storyblok still wins over the derived URL.
  const overrideUrl = content?.custom_floating_cta ? content.floating_cta_link?.cached_url : undefined
  const cachedUrl = overrideUrl || href
  const label = content?.floating_cta_label || (href ? "Boka tid" : undefined)

  if (content?.disable_floating_cta) return null
  if (!cachedUrl || !label) return null

  // Onelink URLs must break out of any embedding frame, matching FloatingCTAMobile.
  const linkTarget = cachedUrl.includes("onelink") ? "_top" : "_self"

  const handleClick = () => {
    if (trackingEvent) trackEvent(trackingEvent, { page_path: pathname })
  }

  // Hoisted so both branches share it. `hover:bg-primary-50` is deliberate: ButtonIframeLink carries a
  // grey `hover:bg-gray-200` that twMerge only drops if a competing `hover:bg-*` is present.
  const pillClassName = cn(
    "flex h-14 w-full items-center justify-center rounded-full bg-primary-50 text-base font-semibold text-white shadow-elevation-1 lg:w-fit lg:px-6",
    "hover:bg-primary-50 hover:opacity-90 active:opacity-80 active:shadow-elevation-2",
    linkClassName
  )

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-50 p-4 transition-opacity duration-300 lg:sticky lg:bottom-4 lg:z-40 lg:flex lg:justify-end lg:px-8",
        isScrolling ? "pointer-events-none lg:pointer-events-auto" : "lg:pointer-events-auto",
        className
      )}
    >
      {openInIframe ? (
        <>
          <Link href={cachedUrl} target="_self" onClick={handleOnIframeButtonClick} className={pillClassName}>
            {label}
          </Link>
          {open && <IframeModal src={cachedUrl} onClose={() => setOpen(false)} useCloseButton modalClassName="p-0" />}
        </>
      ) : (
        <Link
          // After urlParser, so the filter is appended to the final URL rather than to a path the
          // parser then rewrites.
          href={withVaccineFilter(urlParser(cachedUrl), vaccineSlug)}
          target={linkTarget}
          onClick={handleClick}
          className={pillClassName}
        >
          {label}
        </Link>
      )}
    </div>
  )
}

export default VaccinationFloatingCta
